/**
 * Where a traveller goes back after signing in or signing up (the listing they
 * were booking). Only Ridalo pages listed below are accepted: anything else
 * (another site, « //host », « javascript: », unknown page) is ignored and the
 * usual destination (/dashboard) is kept.
 *
 * The destination travels as `?redirect=` between /login and /register. For
 * Google sign-in it is kept in sessionStorage during the trip to Google (the
 * OAuth request itself is unchanged).
 */
const STORAGE_KEY = 'ridalo:authRedirect';

const ALLOWED_PATHS = [
  /^\/scooters\/?$/,
  /^\/scooters\/[A-Za-z0-9_-]+\/?$/,
  /^\/services\/?$/,
  /^\/services\/[A-Za-z0-9_-]+\/?$/,
  /^\/transport\/?$/,
  /^\/provider\/[A-Za-z0-9_-]+\/?$/,
  /^\/dashboard\/?$/,
  /^\/account\/notifications\/?$/,
];

/** The destination if it is an allowed Ridalo page, else null. */
export const safeRedirect = (value) => {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  // Same-site paths only: « //host » and « /\host » are other sites for a browser.
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  // Control characters (tab, newline…) are dropped by URL parsing: refuse them.
  if ([...raw].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)) return null;
  let url;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (!ALLOWED_PATHS.some((re) => re.test(url.pathname))) return null;
  return url.pathname + url.search + url.hash;
};

/** `path` with the destination as `?redirect=` (only when it is allowed). */
export const withRedirect = (path, redirect) => {
  const safe = safeRedirect(redirect);
  return safe ? `${path}?redirect=${encodeURIComponent(safe)}` : path;
};

/** Before leaving for Google: keep the destination (or forget an old one). */
export const rememberAuthRedirect = (redirect) => {
  try {
    const safe = safeRedirect(redirect);
    if (safe) sessionStorage.setItem(STORAGE_KEY, safe);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode — the traveller lands on /dashboard as before */ }
};

/** Back from Google: the kept destination, read once. */
export const takeAuthRedirect = () => {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return safeRedirect(value);
  } catch {
    return null;
  }
};
